import {systemInfo} from "@/systemInfo.js";
import {showNotification} from "@/services/notification.js";
import {formatDateTime, getFormattedDate, getFormattedDateTime} from "@/services/helperFunctions.js";
import * as state from "@/services/state.js";

// Storage keys for reboot information
const STORAGE_KEYS = {
    LAST_REBOOT_DATE: 'lastRebootDate',
    LAST_REBOOT_CAUSE: 'lastRebootCause',
    LAST_DAILY_REBOOT_DATE: 'lastDailyRebootDate'
};

// Environment variables with defaults
const ENV = {
    DAILY_REBOOT_TIME: import.meta.env.VITE_DAILY_REBOOT_TIME || '6:10',
    NO_MSG_REBOOT_DELAY_MINS: parseInt(import.meta.env.VITE_NO_MSG_REBOOT_DELAY_MINS || '10'),
    SERIAL_NUMBER: import.meta.env.VITE_SERIAL_NUMBER || '',
    CHROME_EXTENSION_ID: import.meta.env.VITE_CHROME_EXTENSION_ID || ''
};

// Parse reboot time from environment variable
const parseRebootTime = () => {
    const [hours, minutes] = ENV.DAILY_REBOOT_TIME.split(':').map(Number);
    return {
        hours: isNaN(hours) ? 6 : hours,
        minutes: isNaN(minutes) ? 10 : minutes,
        totalMinutes: (isNaN(hours) ? 6 : hours) * 60 + (isNaN(minutes) ? 10 : minutes)
    };
};

// Reboot time in hours and minutes
const REBOOT_TIME = parseRebootTime();

// State variables
let rebootParam = null;
let lastMessageReceived = null;
let isRebooting = false;
let countdownInterval = null;

/**
 * Update the last reboot date in system info and localStorage
 * @param {boolean} initial - Whether this is the initial app load
 */
export function updateRebootDate(initial = false) {
    const now = new Date();

    // Update localStorage
    localStorage.setItem(STORAGE_KEYS.LAST_REBOOT_DATE, getFormattedDate());

    // Update systemInfo
    systemInfo.lastRebootDate = getFormattedDate();
    systemInfo.lastRebootCause = localStorage.getItem(STORAGE_KEYS.LAST_REBOOT_CAUSE) || '...';

    // Update UI if elements exist
    const rebootDateElement = document.getElementById("last-reboot");
    if (rebootDateElement) {
        rebootDateElement.innerHTML = `${systemInfo.lastRebootDate}<br>${systemInfo.lastRebootCause}`;
    }

    // Set the last daily reboot date if initial load and time is past reboot time
    if (initial) {
        const currentTime = now.getHours() * 60 + now.getMinutes();

        if (currentTime >= REBOOT_TIME.totalMinutes) {
            localStorage.setItem(STORAGE_KEYS.LAST_DAILY_REBOOT_DATE, now.toLocaleDateString('en-CA'));
        }
    }
}

/**
 * Initiate device reboot
 * @param {string} reason - Reason for reboot
 * @param {boolean} cancel - Immediately cancel
 */
export function rebootDevice(reason = '', cancel=false) {
    // Prevent duplicate reboot commands
    if (isRebooting && !cancel) {
        console.log("Reboot already in progress, ignoring duplicate command");
        return;
    }
    
    isRebooting = true;
    state.setState('rebooting')

    if (cancel) {
        console.log("Cancelling reboot: remote cancel");
        isRebooting = false;
        cancelReboot(null);
        return
    }

    console.log("Initiating reboot:", reason);

    // Handle different reboot reasons
    if (reason === 'daily') {
        doReboot('Daily Reboot', 'Broadcast will automatically resume', 'Daily');
    } else if (reason === 'startup') {
        doReboot('Rebooting', 'Startup/Connection issue', 'Startup Err');
    } else if (reason === 'emptySched') {
        doReboot("Rebooting", "Reached end of game schedule", 'End of Sched');
    } else if (reason === 'noMessages') {
        doReboot("Rebooting", "Broadcast issue", 'No Game Msgs');
    } else if (reason === 'startup-manual') {
        doReboot('Rebooting', 'Press (C) or click anywhere to cancel', 'Manual');
        rebootParam = 'startup-manual';
        window.addEventListener("keydown", cancelReboot);
        window.addEventListener("click", cancelReboot, true);
    } else if (reason === 'remote') {
        doReboot('Rebooting', 'Press (C) or click anywhere to cancel', 'Remote');
        window.addEventListener("remote-cancel-reboot", cancelReboot);
        window.addEventListener("keydown", cancelReboot);
        window.addEventListener("click", cancelReboot, true);
    } else {
        doReboot('Rebooting', 'Press (C) or click anywhere to cancel', 'Manual');
        rebootParam = null;
        window.addEventListener("keydown", cancelReboot);
        window.addEventListener("click", cancelReboot, true);
    }

    /**
     * Perform the actual reboot
     * @param {string} title - Title for notification
     * @param {string} message - Message for notification
     * @param {string} cause - Cause of reboot for logging
     */
    function doReboot(title, message, cause) {
        let countdown = 10;

        function updateNotification(p = true) {
            showNotification(`${title} in ${countdown} seconds<br>${message}`, '', p, true)
        }

        updateNotification();

        countdownInterval = setInterval(() => {
            countdown--;
            updateNotification(false);

            if (countdown <= 0) {
                clearInterval(countdownInterval);

                // Update reboot information
                updateRebootDate();
                localStorage.setItem(STORAGE_KEYS.LAST_REBOOT_CAUSE, cause);

                // For daily reboots, update the daily reboot date
                if (cause.toLowerCase() === 'daily') {
                    localStorage.setItem(STORAGE_KEYS.LAST_DAILY_REBOOT_DATE, new Date().toLocaleDateString('en-CA'));
                }

                console.info(`REBOOT: ${cause}`);

                if (import.meta.env.VITE_SERIAL_NUMBER === '') {
                    if (typeof chrome !== 'undefined' && chrome && chrome.runtime) {
                        chrome.runtime.sendMessage(ENV.CHROME_EXTENSION_ID, {action: "rebootSystem"});
                    } else {
                        console.error("Chrome runtime not available for reboot. Attempting basic reload...");
                        location.reload();
                    }
                } else {
                    // In development, just reload the page
                    location.reload();
                }
            }
        }, 1000);
    }

    /**
     * Cancel a reboot in progress
     * @param {KeyboardEvent} event - Keyboard event
     */
    function cancelReboot(event) {
        if (!event ||
            event.type.toLowerCase() === "remote-cancel-reboot" ||
            event.type === "click" ||
            event.key?.toLowerCase() === "c")
        {
            if (event && event.type === "click") {
                event.stopPropagation();
                event.preventDefault();
            }

            clearInterval(countdownInterval);
            countdownInterval = null;
            isRebooting = false;
            console.log("Reboot cancelled");

            // Clear reboot parameter
            rebootParam = null;

            // Remove all event listeners
            window.removeEventListener("keydown", cancelReboot);
            window.removeEventListener("remote-cancel-reboot", cancelReboot);
            window.removeEventListener("click", cancelReboot, true)
            showNotification('Reboot Cancelled', '', false, true);
            state.setState(state.getLastState())
        }
    }
}

/**
 * Set the last message received time
 * @param {Date} time - The last message received time
 */
export function setLastMessageReceived(time) {
    lastMessageReceived = time;
}

/**
 * Check and perform auto reboot if conditions are met
 */
export function checkAutoReboot() {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    const today = now.toLocaleDateString('en-CA');

    // Get the last daily reboot date
    const lastDailyRebootDate = localStorage.getItem(STORAGE_KEYS.LAST_DAILY_REBOOT_DATE);

    // Check for daily reboot time
    if (currentTime >= REBOOT_TIME.totalMinutes && (!lastDailyRebootDate || lastDailyRebootDate !== today)) {
        rebootDevice('daily');
        return;
    }

    // Check for no messages received
    if (lastMessageReceived && (now - lastMessageReceived > ENV.NO_MSG_REBOOT_DELAY_MINS * 60 * 1000)) {
        rebootDevice('noMessages');
        return;
    }
}

/**
 * Update the next reboot information in the UI
 */
export function updateNextRebootInfo() {
    const now = new Date();

    // Calculate next daily reboot time
    const nextDailyReboot = new Date(now);
    nextDailyReboot.setHours(REBOOT_TIME.hours, REBOOT_TIME.minutes, 0, 0);

    // If today's reboot time has passed, set it to tomorrow
    if (nextDailyReboot <= now) {
        nextDailyReboot.setDate(nextDailyReboot.getDate() + 1);
    }

    let nextRebootTime, nextRebootReason;

    nextRebootTime = nextDailyReboot;
    nextRebootReason = 'Daily';

    systemInfo.nextRebootTime = `${formatDateTime(nextRebootTime)} | ${nextRebootReason}`;
}

/**
 * Initialize the reboot service
 */
export function initRebootService() {
    // Update reboot date on initial load
    // updateRebootDate(true);
    systemInfo.lastReboot = getFormattedDateTime() // Set last reboot time to now.

    // Update next reboot information
    updateNextRebootInfo();

    // Set up interval to check for auto reboot conditions
    setInterval(() => {
        checkAutoReboot();
        // updateNextRebootInfo();
    }, 60000); // Check every minute
}